'use client';

import { useField } from 'formik';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { COUNTRIES, COUNTRY_CODES } from '@commons/i18n/en/locations';

import Select, { type Option, type SelectProps } from './';

// Type definition for the CountrySelect component properties.

type Props = Omit<SelectProps, 'options'> & {
  useLabelAsValue?: boolean; // New prop to control value type
  variant?: 'primary' | 'secondary';
};

/**
 * CountrySelect component.
 *
 * This component provides a dropdown for selecting countries, with options filtered and sorted according to priority or other criteria.
 */

export const CountrySelect = ({
  useLabelAsValue = false,
  variant = 'primary',
  ...props
}: Props) => {
  const t = useTranslations('locations.countries');

  const options: Option[] = useMemo(() => {
    return COUNTRY_CODES.map((code) => ({
      label: t(code),
      value: useLabelAsValue ? COUNTRIES[code] : code,
    }));
  }, [t, useLabelAsValue]);

  return <Select {...props} variant={variant} options={options} searchable />;
};

/**
 * FormikCountrySelect component.
 *
 * This is a Formik-compatible wrapper for `CountrySelect`, integrating form state management and error handling directly from Formik context.
 */

export const FormikCountrySelect = ({ name, ...props }: Props) => {
  const [field, meta, helpers] = useField(name);

  const { touched, error } = meta;
  const { setValue } = helpers;

  const isError = touched && error !== undefined;

  return (
    <div className="notranslate" translate="no">
      <CountrySelect onValueChange={setValue} error={isError} {...field} {...props} />
    </div>
  );
};

export default CountrySelect;
